/* Cajita — Drawer + Dialog (overlays .cx, portados del prototipo).
 * Autocontenidos: cada uno se envuelve en `.cx` para traer tokens + clases,
 * así funcionan sin importar dónde se rendericen. Cierran con Escape. */
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

type Tone = 'primary' | 'success' | 'warning' | 'error' | 'info';

function toneStyle(tone: Tone): { background: string; color: string } {
  if (tone === 'primary') return { background: 'var(--primary-soft)', color: 'var(--primary-ink)' };
  return { background: `var(--${tone}-soft)`, color: `var(--${tone})` };
}

export function CxDialog({
  title,
  sub,
  icon,
  iconTone = 'primary',
  onClose,
  children,
  footer,
  width = 460,
}: {
  title: ReactNode;
  sub?: ReactNode;
  icon?: ReactNode;
  iconTone?: Tone;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}): JSX.Element {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return createPortal(
    <div className="cx">
      <div className="cx-scrim" onClick={onClose} />
      <div className="cx-dialog" style={{ width }}>
        <div className="dialog-head">
          <div className="row between ait">
            <div className="row gap12 ait">
              {icon && (
                <div style={{ width: 40, height: 40, borderRadius: 11, display: 'grid', placeItems: 'center', flex: 'none', ...toneStyle(iconTone) }}>
                  {icon}
                </div>
              )}
              <div>
                <div className="t-h1">{title}</div>
                {sub && (
                  <div className="muted t-sm" style={{ marginTop: 4 }}>
                    {sub}
                  </div>
                )}
              </div>
            </div>
            <button className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Cerrar" style={{ background: 'var(--canvas)' }}>
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="dialog-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {children}
        </div>
        {footer && <div className="dialog-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function CxDrawer({
  title,
  sub,
  onClose,
  children,
  footer,
  width = 480,
}: {
  title: ReactNode;
  sub?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}): JSX.Element {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return createPortal(
    <div className="cx">
      <div className="cx-scrim" onClick={onClose} />
      <div className="cx-drawer" style={{ width }}>
        <div className="drawer-head">
          <div>
            <div className="t-h1">{title}</div>
            {sub && (
              <div className="muted t-sm" style={{ marginTop: 4 }}>
                {sub}
              </div>
            )}
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Cerrar" style={{ background: 'var(--canvas)' }}>
            <X size={18} />
          </button>
        </div>
        <div className="drawer-body">{children}</div>
        {footer && <div className="drawer-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
